import { onSwipe } from '@/gesture'

// signed distance from `active` to slide `i` around a loop of `n` slides: the last slide sits just
// left of the first, so 4 -> 1 is one step forward rather than three steps back
function loopPos (i, active, n) {
  const m = (((i - active) % n) + n) % n
  return m > n / 2 ? m - n : m
}

/**
 * Shared "slide follows the drag, commits past a threshold or snaps back" behaviour (build brief
 * E2/B3): used by every swipeable image/video track on the site (Imageset, ProjectViewer, Home).
 * Builds on the existing pointer-based onSwipe() helper rather than replacing it.
 *
 * Slides are laid out as a loop, so wrapping from the last slide back to the first is the same one-
 * step slide as any other move. A slide whose position jumps more than one step between two states
 * is only ever off screen, so it moves there instantly instead of sweeping across the frame.
 *
 * The consumer must keep its current slide index in `active`.
 *
 * Usage:
 *   this.bindDragTrack(this.$refs.frame, { onSwipeLeft: () => this.next(1), onSwipeRight: () => this.next(-1) })
 *   :style="trackStyle(i, active, items.length)"
 */
export default {
  data () {
    return {
      dragging: false, // a horizontal drag is in progress
      dragPercent: 0, // live drag offset, as a % of the frame's own width, while dragging
      swiped: false, // true for a short beat after a swipe/drag, so a trailing click doesn't also fire
      trackFrom: null, // the previous `active`, to tell which slides move one step and which jump
      twoSide: 1, // with exactly two slides, the side the other one waits on (+1 right, -1 left)
      _dragTrackOff: null,
      _dragFrame: null
    }
  },
  watch: {
    active (now, before) {
      this.trackFrom = before
      this.twoSide = -this.twoSide // with two slides, the outgoing one leaves by the opposite side
    }
  },
  methods: {
    // `el` is where the drag is captured (can be a larger hit area); `widthEl` (defaults to `el`) is
    // the element whose width the live drag % is measured against, when the two differ (e.g. a
    // full-screen overlay capturing the drag, sized against just its inner image frame).
    // onSwipeUp/onSwipeDown are optional passthroughs for a consumer that also cares about a
    // vertical flick (e.g. Home's "swipe up to pull the project up") — they don't affect the track.
    bindDragTrack (el, { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold = 40, widthEl } = {}) {
      if (!el || this._dragTrackOff) return
      const measureEl = widthEl || el
      let latestDx = 0
      this._dragTrackOff = onSwipe(el, {
        threshold,
        onMove: (dx, dy) => {
          if (Math.abs(dx) <= Math.abs(dy)) return // vertical intent: leave it to native scroll
          if (!this.dragging) {
            this.trackFrom = this.active // a drag starts from rest: nothing is mid-jump
            this.dragging = true
          }
          // one re-render per frame at most, however fast the pointer events arrive (phones)
          latestDx = dx
          if (this._dragFrame) return
          this._dragFrame = requestAnimationFrame(() => {
            this._dragFrame = null
            if (!this.dragging) return
            this.dragPercent = (latestDx / (measureEl.offsetWidth || 1)) * 100
            this.twoSide = latestDx < 0 ? 1 : -1 // two slides: the other one comes in from the side dragged towards
          })
        },
        onSwipe: dir => {
          if (dir === 'left' && onSwipeLeft) onSwipeLeft()
          else if (dir === 'right' && onSwipeRight) onSwipeRight()
          else if (dir === 'up' && onSwipeUp) onSwipeUp()
          else if (dir === 'down' && onSwipeDown) onSwipeDown()
        },
        onEnd: ({ moved }) => {
          if (this._dragFrame) cancelAnimationFrame(this._dragFrame)
          this._dragFrame = null
          this.dragging = false
          this.dragPercent = 0 // track settles on the (possibly just-updated) active slide
          this.swiped = moved
          setTimeout(() => { this.swiped = false }, 80)
        }
      })
    },
    unbindDragTrack () {
      if (this._dragFrame) cancelAnimationFrame(this._dragFrame)
      if (this._dragTrackOff) {
        this._dragTrackOff()
        this._dragTrackOff = null
      }
    },
    // slide `i`'s place around the loop relative to `active`: 0 on screen, ±1 the neighbours
    trackPos (i, active, count) {
      if (i === active || count < 2) return i === active ? 0 : 1
      if (count === 2) return this.twoSide
      return loopPos(i, active, count)
    },
    // inline style for slide `i`: its loop position, offset live by the drag, animated only when it
    // moves a single step (anything further is off screen and simply repositions)
    trackStyle (i, active, count) {
      const pos = this.trackPos(i, active, count)
      const pct = pos * 100 + (this.dragging ? this.dragPercent : 0)
      let animate = !this.dragging
      if (animate && count > 2 && this.trackFrom !== null && this.trackFrom !== undefined) {
        animate = Math.abs(pos - loopPos(i, this.trackFrom, count)) <= 1
      }
      return {
        transform: `translateX(${pct}%)`,
        transition: animate ? 'transform 400ms cubic-bezier(.2,.7,.2,1)' : 'none',
        // only the slides that can be on screen get their own compositing layer
        willChange: Math.abs(pos) <= 1 ? 'transform' : 'auto'
      }
    }
  },
  beforeDestroy () {
    this.unbindDragTrack()
  }
}
